import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {pluralize} from 'BenchlingUtil';
import {Registry} from 'constants/RegistryConstants';
import {
  CreateStorablePanes,
  ModalPane,
  SelectedParentStorable,
  ValidationErrors,
} from 'constants/SampleConstants';
import {SchemaTag} from 'folder-items/FileConstants';
import {PotentialChildStorable} from 'inventory/location-validation/ParentStorableValidationUtils';
import RegistrySelector from 'registry/RegistrySelector';
import {CreateStorableModalPaneState} from 'samples/CreateStorableModal';
import * as SampleUtil from 'samples/SampleUtil';
import {getEmptySelectedParent, isParentStorableRequired} from 'samples/storables/ParentStorableUtil';
import {StorableSchema} from 'samples/storables/StorableSchemaConstants';
import {StorableSourceFormGroup} from 'samples/storables/StorableSourceSelector';
import SchemaTagInputs from 'schemas/tags/SchemaTagInputs';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import FormGroup from 'ui-library/bs/FormGroup';
import FormInput from 'ui-library/bs/FormInput';
import Button, {ButtonStyle} from 'ui-library/buttons/Button';
import ErrorMessage from 'ui-library/ErrorMessage';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {LABEL_STYLES} from 'ui-library/inputs/InputConstants';
import TextField from 'ui-library/inputs/TextField';
import {InputChangeEvent} from 'util/Types';

import BarcodeInput from './BarcodeInput';
import ParentStorableDataSelector from './ParentStorableDataSelector';

export interface BaseStorablePaneProps {
  canCreateMultiple?: boolean;
  customBarcode: string;
  disableRegistrySelector?: boolean;
  disabledRegistrySelectorTooltip?: string;
  modalPane: CreateStorablePanes;
  name: string;
  nameLabel: string;
  numRegistries?: number;
  numToCreate: number;
  onUpdate: (state: Partial<CreateStorableModalPaneState>) => void;
  registryId?: string;
  schemaSelector: React.ReactNode;
  selectedParent?: SelectedParentStorable;
  selectedSchema?: StorableSchema | null;
  shouldAutogenerateBarcode: boolean;
  shouldShowProjectSelector?: boolean;
  sourceId?: string | null;
  tags: Array<SchemaTag>;
  validationErrors: ValidationErrors;
  willApplyNameTemplate?: boolean;
}

export interface CreateStorablePaneState {
  customBarcode: string;
  errorMessage: string | null;
  isCreating: boolean;
  name: string;
  numToCreate: number;
  registry: Registry | null;
  selectedParent: SelectedParentStorable;
  selectedSchema: StorableSchema | null;
  shouldAutogenerateBarcode: boolean;
  sourceId: string | null;
  tags: Array<SchemaTag>;
  validationErrors: ValidationErrors;
}

export const DEFAULT_CREATE_STORABLE_STATE: CreateStorablePaneState = {
  customBarcode: '',
  errorMessage: null,
  isCreating: false,
  name: '',
  numToCreate: 1,
  registry: null,
  selectedParent: getEmptySelectedParent(),
  selectedSchema: null,
  shouldAutogenerateBarcode: true,
  sourceId: null,
  tags: [],
  validationErrors: {},
};

/**
 * Composable form for creating a storable.
 *
 * If the storable is studyable, render BaseStorablePaneStudyableFormGroup as a child.
 */
class BaseStorablePane extends React.Component<BaseStorablePaneProps> {
  static defaultProps = {
    canCreateMultiple: false,
    numToCreate: 1,
    shouldShowProjectSelector: true,
  };

  private handleChangeName = (e: InputChangeEvent): void => {
    this.props.onUpdate({
      name: e.target.value,
    });
  };

  private handleChangeNumToCreate = (e: InputChangeEvent): void => {
    const numToCreate = parseInt(e.target.value, 10);
    this.props.onUpdate({numToCreate});
  };

  private handleChangeRegistry = (e: {target: {value: Registry | null}}): void => {
    this.props.onUpdate({
      registry: e.target.value,
      // Clear selected parent and selected schema when changing registry
      selectedParent: getEmptySelectedParent(),
      selectedSchema: null,
    });
  };

  private handleUpdateTags = (tags: Array<SchemaTag>): void => {
    this.props.onUpdate({tags});
  };

  private handleUpdateSelectedParent = (selectedParent: SelectedParentStorable): void => {
    const selectedCells = selectedParent.gridCells.length;
    const extraStateUpdate = selectedCells > this.props.numToCreate ? {numToCreate: selectedCells} : {};
    this.props.onUpdate({selectedParent, ...extraStateUpdate});
  };

  private getStorablesToBeMoved = (
    canDirectlyEditNumToCreate: boolean
  ): Array<PotentialChildStorable> | null => {
    if (canDirectlyEditNumToCreate && this.props.numToCreate > 0) {
      return [...Array(this.props.numToCreate).keys()].map(() => ({
        storableSchema: this.props.selectedSchema ?? undefined,
      }));
    }
    // Pass storablesToBeMoved as list of length 1 for create containers (since we've enabled grid deprioritization
    // everywhere) or for plates/boxes if capacity features are enabled. Passing as undefined otherwise
    // prevents the "Available space" header showing up when we do not want it (create locations).
    if (
      [ModalPane.CREATE_CONTAINERS, ModalPane.CREATE_BOX, ModalPane.CREATE_PLATE].includes(
        this.props.modalPane
      )
    ) {
      return [{storableSchema: this.props.selectedSchema ?? undefined}];
    }
    return null;
  };

  private getGridPositionsInfo = (): JSX.Element | null => {
    // Number of selected grid cells must match number of items being created
    const parentStorable = this.props.selectedParent?.storable;
    if (parentStorable && SampleUtil.isGridId(parentStorable.id)) {
      const numStorables = this.props.numToCreate;
      const numGridCellsSelected = this.props.selectedParent.gridCells.length;
      const positionsSelected =
        numGridCellsSelected === numStorables
          ? `${numGridCellsSelected}`
          : `${numGridCellsSelected} of ${numStorables}`;
      const selectedStr = `${positionsSelected} ${pluralize(
        Math.max(numStorables, numGridCellsSelected),
        'container'
      )} placed.`;
      return (
        <div className={css(styles.gridPositionsContainer)}>
          <span>{selectedStr}</span>
          {numGridCellsSelected > 0 && numGridCellsSelected !== numStorables && (
            <span>
              <Button
                buttonStyle={ButtonStyle.LINK}
                text={`Update number of containers to ${numGridCellsSelected}`}
                onClick={() => {
                  const {parentStorableGridError, ...validationErrors} = this.props.validationErrors;
                  this.props.onUpdate({numToCreate: numGridCellsSelected, validationErrors});
                }}
                className={css(styles.updateNumToCreateButton)}
              />
            </span>
          )}
        </div>
      );
    }
    return null;
  };

  override render(): JSX.Element {
    const canDirectlyEditNumToCreate = this.props.canCreateMultiple;

    return (
      <div {...getTestProps({component: 'BaseStorablePane', element: 'root'})}>
        <div className={css(styles.row)}>
          <div className={css(styles.column)}>
            <BarcodeInput
              customBarcode={this.props.customBarcode}
              error={this.props.validationErrors.barcodeError}
              onUpdate={this.props.onUpdate}
              shouldAutogenerateBarcode={this.props.shouldAutogenerateBarcode}
            />
          </div>
          <FormGroup className={css(styles.column)}>
            <FieldLabel isRequired={true} label="Schema" labelStyle={FieldLabel.STYLES.LABEL} />
            {this.props.schemaSelector}
          </FormGroup>
        </div>
        <div className={css(styles.row)}>
          <FormGroup className={css(styles.column)}>
            <TextField
              autoFocus={true}
              label={this.props.nameLabel}
              labelStyle={LABEL_STYLES.LABEL}
              placeholder={this.props.willApplyNameTemplate ? '(Autogenerated)' : 'Name'}
              value={this.props.willApplyNameTemplate ? '(Autogenerated)' : this.props.name}
              onChange={this.handleChangeName}
              dataTestAttrs={{component: 'BaseStorablePane', element: 'containerNameTextInput'}}
              disabled={this.props.willApplyNameTemplate ?? false}
            />
          </FormGroup>
          {this.props.shouldShowProjectSelector && (
            <StorableSourceFormGroup
              className={css(styles.column)}
              error={this.props.validationErrors.projectError}
              onStorableChange={this.props.onUpdate}
              registryId={this.props.registryId ?? null}
              selectedSourceId={this.props.sourceId ?? null}
            />
          )}
        </div>
        {this.props.numRegistries !== 1 && (
          <FormGroup>
            <FieldLabel isRequired={true} label="Registry" />
            <RegistrySelector
              disabled={this.props.disableRegistrySelector}
              disabledTooltip={this.props.disabledRegistrySelectorTooltip}
              registryId={this.props.registryId}
              onChange={this.handleChangeRegistry}
            />
          </FormGroup>
        )}
        {this.props.children}
        {this.props.selectedSchema && this.props.selectedSchema.fields.length > 0 && (
          <div>
            <hr className={css(styles.schemaTagTopDivider)} />
            <FieldLabel label="Schema fields" />
            <SchemaTagInputs
              itemLinkAllowed={false}
              isCreating={true}
              onUpdateTags={this.handleUpdateTags}
              tags={this.props.tags}
            />
            {this.props.validationErrors.tagsError && (
              <ErrorMessage error={this.props.validationErrors.tagsError} />
            )}
            <hr className={css(styles.schemaTagBottomDivider)} />
          </div>
        )}
        {canDirectlyEditNumToCreate && (
          <FormGroup>
            <FieldLabel label="# of containers" isRequired={true} />
            <FormInput
              error={
                this.props.validationErrors.numToCreateError ||
                this.props.validationErrors.parentStorableGridError
              }
              min={1}
              onChange={this.handleChangeNumToCreate}
              type="number"
              value={this.props.numToCreate}
            />
            {this.props.validationErrors.numToCreateError && (
              <ErrorMessage error={this.props.validationErrors.numToCreateError} />
            )}
          </FormGroup>
        )}
        <div
          className={css(styles.row)}
          {...getTestProps({component: 'BaseStorablePane', element: 'ParentStorableSelector'})}
        >
          <FormGroup className={css(styles.column)}>
            <ParentStorableDataSelector
              onUpdate={this.handleUpdateSelectedParent}
              canSelectMultipleGridCells={true}
              isRequired={isParentStorableRequired(this.props.modalPane)}
              selectedParent={this.props.selectedParent}
              storableSchema={this.props.selectedSchema}
              validationErrors={this.props.validationErrors}
              gridCellNotice={this.getGridPositionsInfo()}
              storablesToBeMoved={this.getStorablesToBeMoved(Boolean(canDirectlyEditNumToCreate))}
              // When `canDirectlyEditNumToCreate` is true, it means that the number of storables being created
              // can be changed from within the modal. This is a subset of `canCreateMultiple` cases, since
              // creating multiple containers in a grid ignores the `numToCreate` value and instead uses
              // number of positions selected.
              //
              // The location picker currently does not update appropriately when its prop
              // `storablesToBeMoved` changes. To work around this limitation, we pass this prop,
              // `numStorablesToBeMovedMayChange`, through to ParentStorableSearchableSelectTree. When it is
              // true, ParentStorableSearchableSelectTree renders ConnectedParentStorableSelectTree with a
              // single storable to be moved, instead of the true value. This makes it deprioritize
              // only items that are completely full. This way, at least the user is not prevented from choosing
              // destinations that have enough space.
              //
              // See discussion: https://benchling.slack.com/archives/C05E0Q9823D/p1699055591088919
              numStorablesToBeMovedMayChange={Boolean(canDirectlyEditNumToCreate)}
            />
          </FormGroup>
        </div>
        {this.props.validationErrors.formError && (
          <InlineBanner bannerType={InlineBannerType.ERROR} text={this.props.validationErrors.formError} />
        )}
      </div>
    );
  }
}

const styles = StyleSheet.create({
  gridPositionsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    margin: 'auto',
    paddingLeft: 20,
    maxWidth: 400,
  },
  updateNumToCreateButton: {
    margin: '0px 4px',
    verticalAlign: 'top',
  },
  schemaTagTopDivider: {
    marginTop: 0,
    marginBottom: 15,
  },
  schemaTagBottomDivider: {
    marginTop: 5,
    marginBottom: 15,
  },
  row: {
    display: 'flex',
    gap: 24,
  },
  column: {
    flex: 1,
  },
});

export default BaseStorablePane;
